import {
	ChangeEvent,
	DragEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	Grid2,
	IconButton,
	InputAdornment,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Snackbar,
	SvgIcon,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import SideNavigationBar from '../components/SideNavigationBar';
import CustomSwitch from '../components/CustomSwitch';
import FormattedText from '../components/FormattedText';
import { BLACK, GREY, MAIN_THEME_COLOR } from '../assets/theme/Colors';
import { useMainCustomStyle } from '../assets/theme/Theme';
import {
	CheckUrlsResponse,
	KnowledgeChunk,
	KnowledgeDocument,
	PipelineProgress,
	RetrievalTestResponse,
	ServiceHealth,
	SourceUrlOptionsResponse,
	UrlStatusItem,
	UrlStatusResponse,
	cancelDocumentUpload,
	checkUrls,
	deleteContext,
	deleteDocument,
	deleteUrls,
	editContext,
	getContextById,
	getDocumentChunks,
	getDocumentFile,
	getDocumentProgress,
	getDocuments,
	getKnowledgeBaseHealth,
	getPipelineProgress,
	getSourceUrlOptions,
	getUrlStatus,
	retrieveTest,
	setSourceUrl,
	syncFromSitemap,
	updateFromUrls,
	uploadDocuments,
} from '../services/KnowledgeBaseApi';

const DEFAULT_SITEMAP = 'https://www.senzmate.com/sitemap.xml';
const CARD_BORDER = '1px solid #E1E6E8';
const cardSx = {
	border: CARD_BORDER,
	borderRadius: '8px',
	boxShadow: '0 2px 8px rgba(11, 20, 26, 0.04)',
	backgroundColor: '#FFFFFF',
};

type StudioTab = 'website' | 'documents' | 'retrieval';

interface Notice {
	message: string;
	severity: 'success' | 'error' | 'info' | 'warning';
}

interface ConfirmState {
	title: string;
	message: string;
	actionLabel: string;
	onConfirm: () => Promise<void>;
}

interface EditState {
	chunk: KnowledgeChunk;
	values: Record<string, string>;
	keepEmbedding: boolean;
	sourceUrl: string;
	reembedSource: boolean;
}

function StudioIcon({ path }: { path: string }) {
	return (
		<SvgIcon fontSize="small" viewBox="0 0 24 24">
			<path d={path} />
		</SvgIcon>
	);
}

const icons = {
	website:
		'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm6.92 6h-3.1a15.7 15.7 0 0 0-1.38-3.56A8.03 8.03 0 0 1 18.92 8ZM12 4c.83 1.2 1.47 2.54 1.85 4h-3.7A13.7 13.7 0 0 1 12 4ZM4.26 14A7.8 7.8 0 0 1 4 12c0-.69.09-1.36.26-2h3.4a16.4 16.4 0 0 0 0 4h-3.4Zm.82 2h3.1a15.7 15.7 0 0 0 1.38 3.56A8.03 8.03 0 0 1 5.08 16Zm3.1-8h-3.1a8.03 8.03 0 0 1 4.48-3.56A15.7 15.7 0 0 0 8.18 8ZM12 20a13.7 13.7 0 0 1-1.85-4h3.7A13.7 13.7 0 0 1 12 20Zm2.27-6H9.73a14.4 14.4 0 0 1 0-4h4.54a14.4 14.4 0 0 1 0 4Zm.17 5.56A15.7 15.7 0 0 0 15.82 16h3.1a8.03 8.03 0 0 1-4.48 3.56ZM16.34 14a16.4 16.4 0 0 0 0-4h3.4c.17.64.26 1.31.26 2s-.09 1.36-.26 2h-3.4Z',
	document:
		'M6 2h8l4 4v16H6a2 2 0 0 1-2-2V4c0-1.1.9-2 2-2Zm7 1.5V7h3.5L13 3.5ZM8 11v2h8v-2H8Zm0 4v2h8v-2H8Z',
	test: 'm9.5 3-1 2H5v2h1.5l3.1 5.5-4.2 6.3A2 2 0 0 0 7.06 22h9.88a2 2 0 0 0 1.66-3.12l-4.2-6.3L17.5 7H19V5h-3.5l-1-2h-5ZM9 7h6l-2.75 5h-.5L9 7Zm2.08 7h1.84l4 6H7.08l4-6Z',
	refresh:
		'M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.75 10h-2.1A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35Z',
	search: 'M9.5 3a6.5 6.5 0 1 0 3.98 11.64L19.85 21 21 19.85l-6.36-6.37A6.5 6.5 0 0 0 9.5 3Zm0 2a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Z',
	upload: 'M11 16h2V8l3.5 3.5 1.42-1.42L12 4.16l-5.92 5.92L7.5 11.5 11 8v8Zm-5 4h12v-2H6v2Z',
	external:
		'M14 3h7v7h-2V6.41l-8.3 8.3-1.4-1.42L17.58 5H14V3ZM5 5h6v2H5v12h12v-6h2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7c0-1.1.9-2 2-2Z',
	delete: 'M7 21a2 2 0 0 1-2-2V6h14v13a2 2 0 0 1-2 2H7ZM9 9v8h2V9H9Zm4 0v8h2V9h-2ZM8 3h8l1 1h4v2H3V4h4l1-1Z',
	edit: 'm4 16-.75 4.75L8 20l11-11-4-4L4 16Zm13.5-8.5-1 1-1-1 1-1 1 1Z',
};

function getStatusColor(status: string) {
	switch (status.toLowerCase()) {
		case 'scraped':
		case 'healthy':
		case 'completed':
		case 'success':
			return { color: '#008069', backgroundColor: '#E7F8F2' };
		case 'missing':
		case 'failed':
		case 'error':
			return { color: '#C62828', backgroundColor: '#FFF1F1' };
		case 'pending':
		case 'running':
			return { color: '#9A6700', backgroundColor: '#FFF8E1' };
		case 'excess':
			return { color: '#7B4DA0', backgroundColor: '#F6EEFC' };
		default:
			return { color: '#54656F', backgroundColor: '#F0F2F5' };
	}
}

function formatLabel(value: string) {
	return value
		.replace(/[_-]+/g, ' ')
		.replace(/\b\w/g, character => character.toUpperCase());
}

function formatDate(value?: string) {
	if (!value) return '—';
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatElapsedTime(
	startedAt: string | null,
	finishedAt: string | null,
) {
	if (!startedAt) return '';
	const start = new Date(startedAt).getTime();
	const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
	if (Number.isNaN(start) || Number.isNaN(end)) return '';
	const totalSeconds = Math.max(0, Math.floor((end - start) / 1000));
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function getDocumentName(document: KnowledgeDocument) {
	return document.name || document.filename || 'Untitled document';
}

function getChunkId(chunk: KnowledgeChunk) {
	const value = chunk.context_id ?? chunk.id;
	const id = Number(value);
	return Number.isFinite(id) ? id : null;
}

function getChunkTitle(chunk: KnowledgeChunk) {
	const editable = chunk.editable || {};
	const metadata = chunk.metadata || {};
	return String(
		chunk.title ||
			editable.title ||
			metadata.title ||
			`Chunk ${chunk.id || ''}`,
	);
}

function getChunkText(chunk: KnowledgeChunk) {
	const editable = chunk.editable || {};
	return String(
		chunk.context || editable.context || chunk.page_content || '',
	);
}

function extractChunks(payload: Record<string, unknown>) {
	const direct = payload.chunks || payload.data || payload.results;
	if (Array.isArray(direct)) return direct as KnowledgeChunk[];
	if (direct && typeof direct === 'object') {
		return Object.values(direct).flatMap(value =>
			Array.isArray(value) ? (value as KnowledgeChunk[]) : [],
		);
	}
	return [];
}

function ProgressPanel({
	progress,
	title,
	onCancel,
}: {
	progress: PipelineProgress;
	title: string;
	onCancel?: () => void;
}) {
	const stagePercent = progress.stage_total
		? Math.min(100, (progress.stage_index / progress.stage_total) * 100)
		: progress.step_total
			? Math.min(100, (progress.step_index / progress.step_total) * 100)
			: progress.item_total
				? Math.min(
						100,
						(progress.item_current / progress.item_total) * 100,
					)
				: 0;
	const running = ['running', 'processing', 'cancelling'].includes(
		progress.status.toLowerCase(),
	);
	const stageCount = Math.max(0, progress.stage_total || 0);
	const activeStage = Math.max(0, progress.stage_index || 0);
	const elapsedTime = formatElapsedTime(
		progress.started_at,
		progress.finished_at,
	);

	return (
		<Paper
			sx={{
				...cardSx,
				p: { xs: 1.5, sm: 2 },
				mb: 2,
				overflow: 'hidden',
				position: 'relative',
				'&::before': running
					? {
							content: '""',
							position: 'absolute',
							left: 0,
							top: 0,
							bottom: 0,
							width: 3,
							backgroundColor: MAIN_THEME_COLOR,
						}
					: undefined,
			}}>
			<Grid2
				container
				justifyContent="space-between"
				alignItems="flex-start"
				gap={1.5}>
				<Grid2 container alignItems="center" gap={1.25} wrap="nowrap">
					<Box
						sx={{
							width: 36,
							height: 36,
							flexShrink: 0,
							borderRadius: '50%',
							backgroundColor: running ? '#E7F8F2' : '#F0F2F5',
							display: 'grid',
							placeItems: 'center',
						}}>
						<Box
							sx={{
								width: 9,
								height: 9,
								borderRadius: '50%',
								backgroundColor: running
									? MAIN_THEME_COLOR
									: getStatusColor(progress.status).color,
								animation: running
									? 'ingestionPulse 1.4s ease-out infinite'
									: 'none',
								'@keyframes ingestionPulse': {
									'0%': {
										boxShadow: `0 0 0 0 ${MAIN_THEME_COLOR}66`,
									},
									'70%': {
										boxShadow: `0 0 0 8px ${MAIN_THEME_COLOR}00`,
									},
									'100%': {
										boxShadow: `0 0 0 0 ${MAIN_THEME_COLOR}00`,
									},
								},
							}}
						/>
					</Box>
					<Box sx={{ minWidth: 0 }}>
						<Typography fontSize="13px" fontWeight={700}>
							{title}
						</Typography>
						<Typography
							noWrap
							fontSize="11px"
							fontWeight={600}
							color={running ? MAIN_THEME_COLOR : GREY}
							mt={0.2}>
							{progress.stage_name ||
								progress.current_step ||
								formatLabel(progress.status)}
						</Typography>
					</Box>
				</Grid2>
				<Grid2 container alignItems="center" gap={1}>
					<Chip
						size="small"
						label={formatLabel(progress.status)}
						sx={{
							...getStatusColor(progress.status),
							fontSize: '10px',
							fontWeight: 700,
						}}
					/>
					{running && onCancel && (
						<Button size="small" color="error" onClick={onCancel}>
							Cancel
						</Button>
					)}
				</Grid2>
			</Grid2>
			{progress.current_step && (
				<Box
					sx={{
						mt: 1.5,
						px: 1.25,
						py: 0.85,
						borderRadius: '6px',
						backgroundColor: '#F7F9F9',
					}}>
					<Typography fontSize="10px" color={GREY}>
						CURRENT ACTIVITY
					</Typography>
					<Typography
						fontSize="11px"
						fontWeight={600}
						mt={0.2}
						sx={{ wordBreak: 'break-word' }}>
						{progress.current_step}
					</Typography>
				</Box>
			)}
			<Box mt={1.5}>
				<Grid2
					container
					justifyContent="space-between"
					alignItems="center"
					mb={0.7}>
					<Typography fontSize="10px" fontWeight={700} color={GREY}>
						{stageCount
							? `Stage ${Math.min(activeStage, stageCount)} of ${stageCount}`
							: 'Preparing pipeline'}
					</Typography>
					<Typography
						fontSize="10px"
						fontWeight={700}
						color={MAIN_THEME_COLOR}>
						{stagePercent ? `${Math.round(stagePercent)}%` : ''}
					</Typography>
				</Grid2>
				<LinearProgress
					variant={stagePercent ? 'determinate' : 'indeterminate'}
					value={stagePercent}
					sx={{
						height: 8,
						borderRadius: 4,
						backgroundColor: '#E9EDEF',
						'& .MuiLinearProgress-bar': {
							borderRadius: 4,
							backgroundImage: running
								? 'linear-gradient(45deg, rgba(255,255,255,.18) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.18) 50%, rgba(255,255,255,.18) 75%, transparent 75%, transparent)'
								: 'none',
							backgroundSize: '18px 18px',
							animation: running
								? 'progressStripes 1s linear infinite'
								: 'none',
						},
						'@keyframes progressStripes': {
							from: { backgroundPosition: '0 0' },
							to: { backgroundPosition: '18px 0' },
						},
					}}
				/>
				{stageCount > 0 && (
					<Grid2 container gap={0.65} mt={0.85}>
						{Array.from({ length: stageCount }, (_, index) => {
							const stageNumber = index + 1;
							const completed = stageNumber < activeStage;
							const active = stageNumber === activeStage;
							return (
								<Box
									key={stageNumber}
									sx={{ flex: 1, minWidth: 0 }}>
									<Box
										sx={{
											height: 3,
											borderRadius: 2,
											backgroundColor:
												completed || active
													? MAIN_THEME_COLOR
													: '#DCE2E5',
											opacity: completed ? 0.55 : 1,
										}}
									/>
									<Typography
										fontSize="8px"
										color={active ? MAIN_THEME_COLOR : GREY}
										fontWeight={active ? 700 : 500}
										mt={0.35}>
										{active && progress.stage_name
											? progress.stage_name
											: `Stage ${stageNumber}`}
									</Typography>
								</Box>
							);
						})}
					</Grid2>
				)}
			</Box>
			<Grid2 container gap={0.75} mt={1.35}>
				{Boolean(progress.item_total) && (
					<Chip
						size="small"
						variant="outlined"
						label={`File ${progress.item_current} of ${progress.item_total}`}
						sx={{ fontSize: '9px', height: 22 }}
					/>
				)}
				{Boolean(progress.step_total) && (
					<Chip
						size="small"
						variant="outlined"
						label={`Step ${progress.step_index} of ${progress.step_total}`}
						sx={{ fontSize: '9px', height: 22 }}
					/>
				)}
				{progress.item_label && (
					<Tooltip title={progress.item_label}>
						<Chip
							size="small"
							label={progress.item_label}
							sx={{
								fontSize: '9px',
								height: 22,
								maxWidth: { xs: 180, sm: 300 },
								'& .MuiChip-label': {
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								},
							}}
						/>
					</Tooltip>
				)}
				{elapsedTime && (
					<Chip
						size="small"
						variant="outlined"
						label={`Elapsed ${elapsedTime}`}
						sx={{ fontSize: '9px', height: 22 }}
					/>
				)}
			</Grid2>
			{progress.error && (
				<Alert severity="error" sx={{ mt: 1.5, py: 0 }}>
					{progress.error}
				</Alert>
			)}
		</Paper>
	);
}

function MetricCard({
	label,
	value,
	help,
	loading = false,
}: {
	label: string;
	value: string | number;
	help: string;
	loading?: boolean;
}) {
	return (
		<Grid2 size={{ xs: 6, md: 3 }}>
			<Paper sx={{ ...cardSx, p: { xs: 1.5, md: 2 }, height: '100%' }}>
				<Typography fontSize="11px" fontWeight={700} color={GREY}>
					{label.toUpperCase()}
				</Typography>
				{loading ? (
					<Skeleton width="45%" height={34} />
				) : (
					<Typography
						fontSize={{ xs: '22px', md: '26px' }}
						fontWeight={700}>
						{value}
					</Typography>
				)}
				<Typography fontSize="10px" color={GREY}>
					{help}
				</Typography>
			</Paper>
		</Grid2>
	);
}

export default function Studio() {
	const mainDivStyle = useMainCustomStyle();
	const [tab, setTab] = useState<StudioTab>('website');
	const [health, setHealth] = useState<ServiceHealth | null>(null);
	const [serviceError, setServiceError] = useState('');
	const [sitemapUrl, setSitemapUrl] = useState(DEFAULT_SITEMAP);
	const [urlReport, setUrlReport] = useState<UrlStatusResponse | null>(null);
	const [urlLoading, setUrlLoading] = useState(false);
	const [urlSearch, setUrlSearch] = useState('');
	const [urlFilter, setUrlFilter] = useState('all');
	const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
	const [urlPage, setUrlPage] = useState(0);
	const [urlRowsPerPage, setUrlRowsPerPage] = useState(20);
	const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
	const [documentsLoading, setDocumentsLoading] = useState(true);
	const [documentSearch, setDocumentSearch] = useState('');
	const [files, setFiles] = useState<File[]>([]);
	const [ocrPptxImages, setOcrPptxImages] = useState(false);
	const [dragActive, setDragActive] = useState(false);
	const [operation, setOperation] = useState('');
	const [pipelineProgress, setPipelineProgress] =
		useState<PipelineProgress | null>(null);
	const [documentProgress, setDocumentProgress] =
		useState<PipelineProgress | null>(null);
	const [lastDocumentProgress, setLastDocumentProgress] =
		useState<PipelineProgress | null>(null);
	const [documentProgressVisible, setDocumentProgressVisible] =
		useState(false);
	const [showLastDocumentProgress, setShowLastDocumentProgress] =
		useState(false);
	const previousDocumentStatusRef = useRef('idle');
	const documentUploadRequestedRef = useRef(false);
	const [syncDialogOpen, setSyncDialogOpen] = useState(false);
	const [syncPassword, setSyncPassword] = useState('');
	const [question, setQuestion] = useState('');
	const [retrievalResult, setRetrievalResult] =
		useState<RetrievalTestResponse | null>(null);
	const [contextId, setContextId] = useState('');
	const [chunkDialogOpen, setChunkDialogOpen] = useState(false);
	const [chunkDialogTitle, setChunkDialogTitle] = useState('Content chunks');
	const [chunks, setChunks] = useState<KnowledgeChunk[]>([]);
	const [chunksLoading, setChunksLoading] = useState(false);
	const [editState, setEditState] = useState<EditState | null>(null);
	const [sourceOptions, setSourceOptions] =
		useState<SourceUrlOptionsResponse | null>(null);
	const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
	const [notice, setNotice] = useState<Notice | null>(null);

	const showNotice = useCallback(
		(message: string, severity: Notice['severity'] = 'success') => {
			setNotice({ message, severity });
		},
		[],
	);

	const loadHealth = useCallback(async () => {
		try {
			setHealth(await getKnowledgeBaseHealth());
			setServiceError('');
		} catch (error) {
			setServiceError(
				error instanceof Error ? error.message : 'Service unavailable.',
			);
		}
	}, []);

	const loadDocuments = useCallback(async () => {
		setDocumentsLoading(true);
		try {
			const result = await getDocuments();
			setDocuments(result.documents || []);
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Could not load documents.',
				'error',
			);
		} finally {
			setDocumentsLoading(false);
		}
	}, [showNotice]);

	const loadReport = useCallback(async () => {
		if (!sitemapUrl.trim()) return;
		setUrlLoading(true);
		try {
			const result = await getUrlStatus(sitemapUrl.trim());
			setUrlReport(result);
			setSelectedUrls([]);
			setUrlPage(0);
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Could not load sitemap status.',
				'error',
			);
		} finally {
			setUrlLoading(false);
		}
	}, [showNotice, sitemapUrl]);

	const pollProgress = useCallback(async () => {
		const [pipelineResult, documentResult] = await Promise.allSettled([
			getPipelineProgress(),
			getDocumentProgress(),
		]);
		if (pipelineResult.status === 'fulfilled') {
			setPipelineProgress(pipelineResult.value);
		}
		if (documentResult.status === 'fulfilled') {
			setDocumentProgress(documentResult.value);
		}
	}, []);

	useEffect(() => {
		const progress = documentProgress;
		const status = progress?.status.toLowerCase() || 'idle';
		const running = ['running', 'processing', 'cancelling'].includes(
			status,
		);
		const wasRunning = ['running', 'processing', 'cancelling'].includes(
			previousDocumentStatusRef.current,
		);

		if (!progress || status === 'idle') {
			previousDocumentStatusRef.current = status;
			return;
		}

		if (running) {
			setDocumentProgressVisible(true);
			setShowLastDocumentProgress(false);
			previousDocumentStatusRef.current = status;
			return;
		}

		setLastDocumentProgress(progress);
		const uploadJustFinished =
			wasRunning || documentUploadRequestedRef.current;
		documentUploadRequestedRef.current = false;
		previousDocumentStatusRef.current = status;

		if (!uploadJustFinished) {
			setDocumentProgressVisible(false);
			return;
		}

		setDocumentProgressVisible(true);
		const timer = window.setTimeout(
			() => setDocumentProgressVisible(false),
			10000,
		);
		return () => window.clearTimeout(timer);
	}, [documentProgress?.status]);

	useEffect(() => {
		void loadHealth();
		void loadDocuments();
		void pollProgress();
	}, [loadDocuments, loadHealth, pollProgress]);

	useEffect(() => {
		let active = true;
		setUrlLoading(true);
		void getUrlStatus(DEFAULT_SITEMAP)
			.then(result => {
				if (active) setUrlReport(result);
			})
			.catch(error => {
				if (active) {
					showNotice(
						error instanceof Error
							? error.message
							: 'Could not load sitemap status.',
						'error',
					);
				}
			})
			.finally(() => {
				if (active) setUrlLoading(false);
			});
		return () => {
			active = false;
		};
	}, [showNotice]);

	useEffect(() => {
		const pipelineRunning = [
			'running',
			'processing',
			'cancelling',
		].includes(pipelineProgress?.status.toLowerCase() || '');
		const documentRunning = [
			'running',
			'processing',
			'cancelling',
		].includes(documentProgress?.status.toLowerCase() || '');
		if (!pipelineRunning && !documentRunning && !operation) return;
		const interval = window.setInterval(() => void pollProgress(), 2000);
		return () => window.clearInterval(interval);
	}, [
		documentProgress?.status,
		operation,
		pipelineProgress?.status,
		pollProgress,
	]);

	const filteredUrls = useMemo(() => {
		const needle = urlSearch.trim().toLowerCase();
		return (urlReport?.data || []).filter(
			item =>
				(urlFilter === 'all' || item.status === urlFilter) &&
				(!needle || item.url.toLowerCase().includes(needle)),
		);
	}, [urlFilter, urlReport, urlSearch]);

	const visibleUrls = filteredUrls.slice(
		urlPage * urlRowsPerPage,
		urlPage * urlRowsPerPage + urlRowsPerPage,
	);
	const visibleUrlValues = visibleUrls.map(item => item.url);
	const allVisibleSelected =
		visibleUrlValues.length > 0 &&
		visibleUrlValues.every(url => selectedUrls.includes(url));

	const filteredDocuments = useMemo(() => {
		const needle = documentSearch.trim().toLowerCase();
		return documents.filter(document =>
			getDocumentName(document).toLowerCase().includes(needle),
		);
	}, [documentSearch, documents]);

	function toggleUrl(url: string) {
		setSelectedUrls(current =>
			current.includes(url)
				? current.filter(value => value !== url)
				: [...current, url],
		);
	}

	function toggleVisibleUrls() {
		setSelectedUrls(current => {
			if (allVisibleSelected) {
				return current.filter(url => !visibleUrlValues.includes(url));
			}
			return Array.from(new Set([...current, ...visibleUrlValues]));
		});
	}

	async function runUrlUpdate(urls: string[]) {
		setOperation('Updating URLs');
		try {
			const result = await updateFromUrls(urls);
			showNotice(result.message || `${urls.length} URL(s) updated.`);
			await loadReport();
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Could not update URLs.',
				'error',
			);
		} finally {
			setOperation('');
		}
	}

	async function runUrlDelete(urls: string[]) {
		setOperation('Deleting URLs');
		try {
			const result = await deleteUrls(urls);
			showNotice(result.message || `${urls.length} URL(s) deleted.`);
			setSelectedUrls([]);
			await loadReport();
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Could not delete URLs.',
				'error',
			);
		} finally {
			setOperation('');
			setConfirmState(null);
		}
	}

	async function inspectUrls(urls: string[]) {
		setChunkDialogTitle(
			urls.length === 1
				? `Content from ${urls[0]}`
				: 'Selected URL content',
		);
		setChunkDialogOpen(true);
		setChunksLoading(true);
		try {
			const result: CheckUrlsResponse = await checkUrls(urls);
			setChunks(Object.values(result.results).flat());
			if (result.not_found.length) {
				showNotice(
					`${result.not_found.length} URL(s) were not found in the knowledge base.`,
					'warning',
				);
			}
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Could not inspect URLs.',
				'error',
			);
			setChunks([]);
		} finally {
			setChunksLoading(false);
		}
	}

	async function runSitemapSync() {
		if (!syncPassword) return;
		setOperation('Syncing sitemap');
		setSyncDialogOpen(false);
		try {
			const result = await syncFromSitemap(
				sitemapUrl.trim(),
				syncPassword,
			);
			showNotice(result.message || 'Sitemap sync completed.');
			setSyncPassword('');
			await loadReport();
		} catch (error) {
			showNotice(
				error instanceof Error ? error.message : 'Sitemap sync failed.',
				'error',
			);
		} finally {
			setOperation('');
			void pollProgress();
		}
	}

	function addFiles(incoming: File[]) {
		const allowed = incoming.filter(file =>
			/\.(pdf|docx|pptx|png|jpe?g|webp|tiff?)$/i.test(file.name),
		);
		setFiles(current => {
			const byName = new Map(current.map(file => [file.name, file]));
			allowed.forEach(file => byName.set(file.name, file));
			return Array.from(byName.values());
		});
		if (allowed.length !== incoming.length) {
			showNotice('Some unsupported files were skipped.', 'warning');
		}
	}

	function onFilesSelected(event: ChangeEvent<HTMLInputElement>) {
		addFiles(Array.from(event.target.files || []));
		event.target.value = '';
	}

	function onDrop(event: DragEvent<HTMLDivElement>) {
		event.preventDefault();
		setDragActive(false);
		addFiles(Array.from(event.dataTransfer.files));
	}

	async function runDocumentUpload() {
		if (!files.length) return;
		documentUploadRequestedRef.current = true;
		setDocumentProgress(null);
		setDocumentProgressVisible(true);
		setShowLastDocumentProgress(false);
		setOperation('Uploading documents');
		try {
			const result = await uploadDocuments(files, ocrPptxImages);
			showNotice(result.message || 'Documents queued for processing.');
			setFiles([]);
			await pollProgress();
			await loadDocuments();
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Document upload failed.',
				'error',
			);
		} finally {
			setOperation('');
		}
	}

	async function inspectDocument(document: KnowledgeDocument) {
		const name = getDocumentName(document);
		setChunkDialogTitle(name);
		setChunkDialogOpen(true);
		setChunksLoading(true);
		try {
			const result = await getDocumentChunks(name);
			setChunks(extractChunks(result));
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Could not load document content.',
				'error',
			);
			setChunks([]);
		} finally {
			setChunksLoading(false);
		}
	}

	async function downloadDocument(document: KnowledgeDocument) {
		const name = getDocumentName(document);
		setOperation(`Opening ${name}`);
		try {
			const blob = await getDocumentFile(name);
			const objectUrl = URL.createObjectURL(blob);
			const link = window.document.createElement('a');
			link.href = objectUrl;
			link.download = name;
			window.document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(objectUrl);
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Could not download document.',
				'error',
			);
		} finally {
			setOperation('');
		}
	}

	async function removeDocument(document: KnowledgeDocument) {
		const name = getDocumentName(document);
		setOperation(`Deleting ${name}`);
		try {
			const result = await deleteDocument(name);
			showNotice(result.message || `${name} was deleted.`);
			await loadDocuments();
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Could not delete document.',
				'error',
			);
		} finally {
			setOperation('');
			setConfirmState(null);
		}
	}

	async function runRetrievalTest() {
		if (!question.trim()) return;
		setOperation('Testing retrieval');
		setRetrievalResult(null);
		try {
			setRetrievalResult(await retrieveTest(question.trim()));
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Could not test retrieval.',
				'error',
			);
		} finally {
			setOperation('');
		}
	}

	async function findContext() {
		if (!contextId.trim()) return;
		setOperation('Finding context');
		try {
			const result = await getContextById(contextId.trim());
			if (!result.found || !result.data) {
				showNotice(
					'No content was found for that context ID.',
					'warning',
				);
				return;
			}
			setChunkDialogTitle(`Context ${contextId.trim()}`);
			setChunks([result.data]);
			setChunkDialogOpen(true);
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Context lookup failed.',
				'error',
			);
		} finally {
			setOperation('');
		}
	}

	async function openEditor(chunk: KnowledgeChunk) {
		const editable = chunk.editable || chunk;
		const fields = [
			'title',
			'context_type',
			'sub_type',
			'context',
			'brief_summary',
			'keywords',
		];
		const values = fields.reduce<Record<string, string>>(
			(result, field) => {
				const value = editable[field] ?? chunk.metadata?.[field] ?? '';
				result[field] = String(value);
				return result;
			},
			{},
		);
		setEditState({
			chunk,
			values,
			keepEmbedding: false,
			sourceUrl: String(
				chunk.source_url || chunk.metadata?.source_url || '',
			),
			reembedSource: false,
		});
		setSourceOptions(null);
		const id = getChunkId(chunk);
		if (id !== null) {
			try {
				const options = await getSourceUrlOptions(id);
				setSourceOptions(options);
				setEditState(current =>
					current && !current.sourceUrl
						? { ...current, sourceUrl: options.source_url || '' }
						: current,
				);
			} catch {
				// Some document chunks do not have linked web source options.
			}
		}
	}

	async function saveContext() {
		if (!editState) return;
		const id = getChunkId(editState.chunk);
		if (id === null) {
			showNotice(
				'This result does not expose an editable context ID.',
				'error',
			);
			return;
		}
		setOperation('Saving context');
		try {
			const originalValues = editState.chunk.editable || editState.chunk;
			const changedValues = Object.entries(editState.values).reduce<
				Record<string, string>
			>((result, [field, value]) => {
				const original =
					originalValues[field] ??
					editState.chunk.metadata?.[field] ??
					'';
				if (String(original) !== value) result[field] = value;
				return result;
			}, {});
			const originalSource = String(
				sourceOptions?.source_url ||
					editState.chunk.source_url ||
					editState.chunk.metadata?.source_url ||
					'',
			);
			const sourceChanged =
				Boolean(sourceOptions) &&
				editState.sourceUrl !== originalSource;
			if (!Object.keys(changedValues).length && !sourceChanged) {
				showNotice('No changes to save.', 'info');
				setEditState(null);
				return;
			}
			if (Object.keys(changedValues).length) {
				await editContext(id, changedValues, editState.keepEmbedding);
			}
			if (sourceChanged) {
				await setSourceUrl(
					id,
					editState.sourceUrl,
					editState.reembedSource,
				);
			}
			showNotice(`Context ${id} was updated.`);
			setChunks(current =>
				current.map(chunk =>
					getChunkId(chunk) === id
						? { ...chunk, ...editState.values }
						: chunk,
				),
			);
			setEditState(null);
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Could not save context.',
				'error',
			);
		} finally {
			setOperation('');
		}
	}

	async function removeContext(chunk: KnowledgeChunk) {
		const id = getChunkId(chunk);
		if (id === null) return;
		setOperation('Deleting context');
		try {
			const result = await deleteContext(id);
			showNotice(result.message || `Context ${id} was deleted.`);
			setChunks(current =>
				current.filter(item => getChunkId(item) !== id),
			);
		} catch (error) {
			showNotice(
				error instanceof Error
					? error.message
					: 'Could not delete context.',
				'error',
			);
		} finally {
			setOperation('');
			setConfirmState(null);
		}
	}

	const summary = urlReport?.summary || {};
	const documentChunkCount = documents.reduce(
		(total, document) =>
			total + Number(document.chunk_count ?? document.chunks ?? 0),
		0,
	);

	return (
		<>
			<SideNavigationBar />
			<Box
				component="main"
				style={mainDivStyle}
				sx={{ overflowY: 'auto', backgroundColor: '#F0F2F5' }}>
				<Box
					sx={{
						px: { xs: 1.5, sm: 2.5, lg: 3 },
						py: { xs: 2, lg: 2.5 },
					}}>
					<Grid2
						container
						alignItems={{ xs: 'flex-start', sm: 'center' }}
						justifyContent="space-between"
						gap={1.5}
						mb={2.25}>
						<Box>
							<Typography
								fontSize={{ xs: '22px', md: '25px' }}
								fontWeight={700}>
								<Box
									component="span"
									sx={{
										display: { xs: 'inline', sm: 'none' },
									}}>
									Studio
								</Box>
								<Box
									component="span"
									sx={{
										display: { xs: 'none', sm: 'inline' },
									}}>
									Knowledge Studio
								</Box>
							</Typography>
							<Typography
								fontSize="12px"
								color={GREY}
								mt={0.25}
								sx={{ display: { xs: 'none', sm: 'block' } }}>
								Manage what your website bot knows and validate
								what it retrieves.
							</Typography>
						</Box>
						<Grid2 container alignItems="center" gap={1}>
							<Chip
								label={
									health
										? `Service ${formatLabel(health.status)}`
										: serviceError
											? 'Service unavailable'
											: 'Checking service'
								}
								size="small"
								sx={{
									...getStatusColor(
										health?.status || 'unknown',
									),
									fontSize: '11px',
									fontWeight: 700,
								}}
							/>
							<Tooltip title="Refresh dashboard">
								<IconButton
									onClick={() => {
										void loadHealth();
										void loadDocuments();
										void pollProgress();
										if (urlReport) void loadReport();
									}}
									sx={{ ...cardSx, width: 34, height: 34 }}>
									<StudioIcon path={icons.refresh} />
								</IconButton>
							</Tooltip>
						</Grid2>
					</Grid2>

					<Grid2 container spacing={1.5} mb={2}>
						<MetricCard
							label="Website URLs"
							value={summary.total ?? '—'}
							help="Discovered in the current sitemap"
							loading={urlLoading && !urlReport}
						/>
						<MetricCard
							label="Indexed pages"
							value={summary.scraped ?? '—'}
							help={`${summary.missing ?? 0} missing · ${summary.pending ?? 0} pending`}
							loading={urlLoading && !urlReport}
						/>
						<MetricCard
							label="Documents"
							value={documents.length}
							help={`${documentChunkCount} indexed chunks`}
							loading={documentsLoading}
						/>
						<MetricCard
							label="Knowledge API"
							value={health?.version || '—'}
							help={health?.service || 'Connection status'}
							loading={!health && !serviceError}
						/>
					</Grid2>

					{pipelineProgress && pipelineProgress.status !== 'idle' && (
						<ProgressPanel
							progress={pipelineProgress}
							title="Website ingestion"
						/>
					)}
					<Paper sx={{ ...cardSx, overflow: 'hidden' }}>
						<Tabs
							value={tab}
							onChange={(_, value: StudioTab) => setTab(value)}
							variant="scrollable"
							scrollButtons="auto"
							sx={{
								px: { xs: 0.5, sm: 1.5 },
								borderBottom: CARD_BORDER,
								minHeight: 52,
								'& .MuiTab-root': {
									minHeight: 52,
									fontSize: '12px',
								},
							}}>
							<Tab
								value="website"
								icon={<StudioIcon path={icons.website} />}
								iconPosition="start"
								label={
									<>
										<Box
											component="span"
											sx={{
												display: {
													xs: 'inline',
													sm: 'none',
												},
											}}>
											Web
										</Box>
										<Box
											component="span"
											sx={{
												display: {
													xs: 'none',
													sm: 'inline',
												},
											}}>
											Website sources
										</Box>
									</>
								}
							/>
							<Tab
								value="documents"
								icon={<StudioIcon path={icons.document} />}
								iconPosition="start"
								label="Documents"
							/>
							<Tab
								value="retrieval"
								icon={<StudioIcon path={icons.test} />}
								iconPosition="start"
								label={
									<>
										<Box
											component="span"
											sx={{
												display: {
													xs: 'inline',
													sm: 'none',
												},
											}}>
											Test
										</Box>
										<Box
											component="span"
											sx={{
												display: {
													xs: 'none',
													sm: 'inline',
												},
											}}>
											Retrieval lab
										</Box>
									</>
								}
							/>
						</Tabs>

						{tab === 'website' && (
							<Box sx={{ p: { xs: 1.5, md: 2.25 } }}>
								<Grid2
									container
									alignItems={{ xs: 'stretch', md: 'center' }}
									spacing={1.25}>
									<Grid2 size={{ xs: 12, md: 'grow' }}>
										<TextField
											fullWidth
											size="small"
											label="Sitemap URL"
											value={sitemapUrl}
											onChange={event =>
												setSitemapUrl(
													event.target.value,
												)
											}
										/>
									</Grid2>
									<Grid2 size={{ xs: 6, md: 'auto' }}>
										<Button
											fullWidth
											variant="outlined"
											disabled={
												urlLoading || !sitemapUrl.trim()
											}
											onClick={() => void loadReport()}
											startIcon={
												urlLoading ? (
													<CircularProgress
														size={15}
													/>
												) : (
													<StudioIcon
														path={icons.refresh}
													/>
												)
											}>
											Compare
										</Button>
									</Grid2>
									<Grid2 size={{ xs: 6, md: 'auto' }}>
										<Button
											fullWidth
											variant="contained"
											disabled={
												!sitemapUrl.trim() ||
												Boolean(operation)
											}
											onClick={() =>
												setSyncDialogOpen(true)
											}>
											Sync sitemap
										</Button>
									</Grid2>
								</Grid2>

								{!urlReport && !urlLoading ? (
									<Box
										sx={{
											py: 8,
											textAlign: 'center',
											color: GREY,
										}}>
										<Box
											sx={{
												width: 46,
												height: 46,
												borderRadius: '50%',
												backgroundColor: '#E7F8F2',
												color: MAIN_THEME_COLOR,
												display: 'grid',
												placeItems: 'center',
												mx: 'auto',
												mb: 1.25,
											}}>
											<StudioIcon path={icons.website} />
										</Box>
										<Typography
											fontSize="14px"
											fontWeight={700}
											color={BLACK}>
											Compare your sitemap
										</Typography>
										<Typography fontSize="12px" mt={0.5}>
											See what is indexed, missing,
											pending, or no longer published.
										</Typography>
									</Box>
								) : urlLoading && !urlReport ? (
									<Box sx={{ py: 5 }}>
										{[1, 2, 3, 4].map(item => (
											<Skeleton key={item} height={42} />
										))}
									</Box>
								) : (
									<>
										<Grid2
											container
											alignItems="center"
											justifyContent="space-between"
											gap={1}
											mt={2.25}
											mb={1.25}>
											<Grid2 container gap={0.75}>
												{[
													'all',
													'missing',
													'pending',
													'scraped',
													'excess',
												].map(status => (
													<Chip
														key={status}
														label={`${formatLabel(status)} ${
															status === 'all'
																? (summary.total ??
																	0)
																: (summary[
																		status
																	] ?? 0)
														}`}
														onClick={() => {
															setUrlFilter(
																status,
															);
															setUrlPage(0);
														}}
														sx={{
															fontSize: '10px',
															fontWeight: 700,
															...(urlFilter ===
															status
																? {
																		color: '#FFFFFF',
																		backgroundColor:
																			MAIN_THEME_COLOR,
																	}
																: getStatusColor(
																		status,
																	)),
														}}
													/>
												))}
											</Grid2>
											<TextField
												size="small"
												placeholder="Search URLs"
												value={urlSearch}
												onChange={event => {
													setUrlSearch(
														event.target.value,
													);
													setUrlPage(0);
												}}
												slotProps={{
													input: {
														startAdornment: (
															<InputAdornment position="start">
																<StudioIcon
																	path={
																		icons.search
																	}
																/>
															</InputAdornment>
														),
													},
												}}
											/>
										</Grid2>

										{selectedUrls.length > 0 && (
											<Grid2
												container
												alignItems="center"
												gap={1}
												sx={{
													p: 1,
													mb: 1,
													borderRadius: '6px',
													backgroundColor: '#E7F8F2',
												}}>
												<Typography
													fontSize="11px"
													fontWeight={700}
													mr="auto">
													{selectedUrls.length}{' '}
													selected
												</Typography>
												<Button
													size="small"
													onClick={() =>
														void inspectUrls(
															selectedUrls,
														)
													}>
													Inspect content
												</Button>
												<Button
													size="small"
													variant="contained"
													disabled={Boolean(
														operation,
													)}
													onClick={() =>
														void runUrlUpdate(
															selectedUrls,
														)
													}>
													Update
												</Button>
												<Button
													size="small"
													color="error"
													onClick={() =>
														setConfirmState({
															title: 'Delete selected URLs?',
															message: `This permanently removes ${selectedUrls.length} URL(s), their linked data, and vectors from the knowledge base.`,
															actionLabel:
																'Delete URLs',
															onConfirm: () =>
																runUrlDelete(
																	selectedUrls,
																),
														})
													}>
													Delete
												</Button>
											</Grid2>
										)}

										<TableContainer
											sx={{
												border: CARD_BORDER,
												borderRadius: '6px',
											}}>
											<Table size="small">
												<TableHead
													sx={{
														backgroundColor:
															'#F7F9F9',
													}}>
													<TableRow>
														<TableCell padding="checkbox">
															<Checkbox
																size="small"
																checked={
																	allVisibleSelected
																}
																indeterminate={
																	!allVisibleSelected &&
																	visibleUrlValues.some(
																		url =>
																			selectedUrls.includes(
																				url,
																			),
																	)
																}
																onChange={
																	toggleVisibleUrls
																}
															/>
														</TableCell>
														<TableCell
															sx={{
																fontSize:
																	'11px',
																fontWeight: 700,
															}}>
															URL
														</TableCell>
														<TableCell
															sx={{
																fontSize:
																	'11px',
																fontWeight: 700,
															}}>
															Status
														</TableCell>
														<TableCell
															align="right"
															sx={{ width: 105 }}
														/>
													</TableRow>
												</TableHead>
												<TableBody>
													{visibleUrls.map(item => (
														<TableRow
															key={item.url}
															hover>
															<TableCell padding="checkbox">
																<Checkbox
																	size="small"
																	checked={selectedUrls.includes(
																		item.url,
																	)}
																	onChange={() =>
																		toggleUrl(
																			item.url,
																		)
																	}
																/>
															</TableCell>
															<TableCell>
																<Typography
																	fontSize="12px"
																	sx={{
																		wordBreak:
																			'break-word',
																	}}>
																	{item.url}
																</Typography>
															</TableCell>
															<TableCell>
																<Chip
																	size="small"
																	label={formatLabel(
																		item.status,
																	)}
																	sx={{
																		...getStatusColor(
																			item.status,
																		),
																		fontSize:
																			'10px',
																		fontWeight: 700,
																	}}
																/>
															</TableCell>
															<TableCell align="right">
																<Tooltip title="Open page">
																	<IconButton
																		size="small"
																		component="a"
																		href={
																			item.url
																		}
																		target="_blank"
																		rel="noreferrer">
																		<StudioIcon
																			path={
																				icons.external
																			}
																		/>
																	</IconButton>
																</Tooltip>
																<Tooltip title="Inspect content">
																	<IconButton
																		size="small"
																		onClick={() =>
																			void inspectUrls(
																				[
																					item.url,
																				],
																			)
																		}>
																		<StudioIcon
																			path={
																				icons.search
																			}
																		/>
																	</IconButton>
																</Tooltip>
															</TableCell>
														</TableRow>
													))}
													{visibleUrls.length ===
														0 && (
														<TableRow>
															<TableCell
																colSpan={4}
																align="center"
																sx={{ py: 5 }}>
																<Typography
																	fontSize="12px"
																	color={
																		GREY
																	}>
																	No URLs
																	match this
																	view.
																</Typography>
															</TableCell>
														</TableRow>
													)}
												</TableBody>
											</Table>
											<TablePagination
												component="div"
												count={filteredUrls.length}
												page={urlPage}
												onPageChange={(_, page) =>
													setUrlPage(page)
												}
												rowsPerPage={urlRowsPerPage}
												onRowsPerPageChange={event => {
													setUrlRowsPerPage(
														Number(
															event.target.value,
														),
													);
													setUrlPage(0);
												}}
												rowsPerPageOptions={[
													10, 20, 50,
												]}
											/>
										</TableContainer>
									</>
								)}
							</Box>
						)}

						{tab === 'documents' && (
							<Box sx={{ p: { xs: 1.5, md: 2.25 } }}>
								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, lg: 4 }}>
										<Typography
											fontSize="15px"
											fontWeight={700}>
											Add documents
										</Typography>
										<Typography
											fontSize="11px"
											color={GREY}
											mt={0.4}
											mb={1.5}>
											PDF, DOCX, PPTX and image files are
											chunked and indexed automatically.
										</Typography>
										<Box
											onDragOver={event => {
												event.preventDefault();
												setDragActive(true);
											}}
											onDragLeave={() =>
												setDragActive(false)
											}
											onDrop={onDrop}
											sx={{
												border: `1px dashed ${dragActive ? MAIN_THEME_COLOR : '#B8C2C7'}`,
												backgroundColor: dragActive
													? '#E7F8F2'
													: '#F7F9F9',
												borderRadius: '8px',
												textAlign: 'center',
												p: 3,
											}}>
											<Box
												sx={{
													color: MAIN_THEME_COLOR,
													mb: 0.75,
												}}>
												<StudioIcon
													path={icons.upload}
												/>
											</Box>
											<Typography
												fontSize="12px"
												fontWeight={700}>
												Drop files here
											</Typography>
											<Typography
												fontSize="10px"
												color={GREY}
												mt={0.25}
												mb={1}>
												or choose them from your
												computer
											</Typography>
											<Button
												component="label"
												size="small"
												variant="outlined">
												Choose files
												<input
													hidden
													multiple
													type="file"
													accept=".pdf,.docx,.pptx,.png,.jpg,.jpeg,.webp,.tif,.tiff"
													onChange={onFilesSelected}
												/>
											</Button>
										</Box>
										{files.length > 0 && (
											<Box mt={1.25}>
												{files.map(file => (
													<Grid2
														key={file.name}
														container
														alignItems="center"
														justifyContent="space-between"
														sx={{
															py: 0.6,
															borderBottom:
																CARD_BORDER,
														}}>
														<Box
															sx={{
																minWidth: 0,
															}}>
															<Typography
																noWrap
																fontSize="11px"
																fontWeight={
																	600
																}>
																{file.name}
															</Typography>
															<Typography
																fontSize="9px"
																color={GREY}>
																{(
																	file.size /
																	1024 /
																	1024
																).toFixed(
																	2,
																)}{' '}
																MB
															</Typography>
														</Box>
														<IconButton
															size="small"
															onClick={() =>
																setFiles(
																	current =>
																		current.filter(
																			item =>
																				item.name !==
																				file.name,
																		),
																)
															}>
															×
														</IconButton>
													</Grid2>
												))}
											</Box>
										)}
										<Grid2
											container
											alignItems="center"
											justifyContent="space-between"
											mt={1.5}>
											<Box>
												<Typography
													fontSize="11px"
													fontWeight={600}>
													OCR images in presentations
												</Typography>
												<Typography
													fontSize="9px"
													color={GREY}>
													Useful but slower for PPTX
													files
												</Typography>
											</Box>
											<CustomSwitch
												isChecked={ocrPptxImages}
												handleChange={event =>
													setOcrPptxImages(
														event.target.checked,
													)
												}
											/>
										</Grid2>
										<Button
											fullWidth
											variant="contained"
											sx={{ mt: 1.5 }}
											disabled={
												!files.length ||
												Boolean(operation)
											}
											onClick={() =>
												void runDocumentUpload()
											}>
											Upload{' '}
											{files.length
												? `${files.length} file(s)`
												: 'documents'}
										</Button>
									</Grid2>

									<Grid2 size={{ xs: 12, lg: 8 }}>
										<Grid2
											container
											alignItems="center"
											justifyContent="space-between"
											mb={1.25}>
											<Box>
												<Typography
													fontSize="15px"
													fontWeight={700}>
													Knowledge documents
												</Typography>
												<Typography
													fontSize="11px"
													color={GREY}>
													{documents.length} uploaded
													source(s)
												</Typography>
											</Box>
											<TextField
												size="small"
												placeholder="Search documents"
												value={documentSearch}
												onChange={event =>
													setDocumentSearch(
														event.target.value,
													)
												}
											/>
											<Button
												size="small"
												variant={
													showLastDocumentProgress
														? 'contained'
														: 'outlined'
												}
												disabled={!lastDocumentProgress}
												onClick={() =>
													setShowLastDocumentProgress(
														current => !current,
													)
												}>
												Last Doc
											</Button>
										</Grid2>
										{(showLastDocumentProgress
											? lastDocumentProgress
											: documentProgress) &&
											(showLastDocumentProgress
												? Boolean(lastDocumentProgress)
												: documentProgressVisible) && (
												<ProgressPanel
													progress={
														(showLastDocumentProgress
															? lastDocumentProgress
															: documentProgress) as PipelineProgress
													}
													title="Document ingestion"
													onCancel={() => {
														setConfirmState({
															title: 'Cancel document ingestion?',
															message:
																'Documents already processed will be kept. The current document will be rolled back.',
															actionLabel:
																'Cancel ingestion',
															onConfirm:
																async () => {
																	try {
																		await cancelDocumentUpload();
																		showNotice(
																			'Cancellation requested.',
																			'info',
																		);
																		void pollProgress();
																	} catch (error) {
																		showNotice(
																			error instanceof
																				Error
																				? error.message
																				: 'Could not cancel ingestion.',
																			'error',
																		);
																	} finally {
																		setConfirmState(
																			null,
																		);
																	}
																},
														});
													}}
												/>
											)}
										<TableContainer
											sx={{
												border: CARD_BORDER,
												borderRadius: '6px',
											}}>
											<Table size="small">
												<TableHead
													sx={{
														backgroundColor:
															'#F7F9F9',
													}}>
													<TableRow>
														<TableCell
															sx={{
																fontSize:
																	'11px',
																fontWeight: 700,
															}}>
															Document
														</TableCell>
														<TableCell
															sx={{
																fontSize:
																	'11px',
																fontWeight: 700,
															}}>
															Chunks
														</TableCell>
														<TableCell
															sx={{
																fontSize:
																	'11px',
																fontWeight: 700,
															}}>
															Original
														</TableCell>
														<TableCell align="right" />
													</TableRow>
												</TableHead>
												<TableBody>
													{documentsLoading ? (
														<TableRow>
															<TableCell
																colSpan={4}
																sx={{ py: 4 }}>
																<Skeleton />
																<Skeleton />
															</TableCell>
														</TableRow>
													) : filteredDocuments.length ? (
														filteredDocuments.map(
															document => (
																<TableRow
																	key={getDocumentName(
																		document,
																	)}
																	hover>
																	<TableCell>
																		<Typography
																			fontSize="12px"
																			fontWeight={
																				600
																			}>
																			{getDocumentName(
																				document,
																			)}
																		</Typography>
																		<Typography
																			fontSize="9px"
																			color={
																				GREY
																			}>
																			{formatDate(
																				document.updated_at ||
																					document.created_at,
																			)}
																		</Typography>
																	</TableCell>
																	<TableCell
																		sx={{
																			fontSize:
																				'12px',
																		}}>
																		{Number(
																			document.chunk_count ??
																				document.chunks ??
																				0,
																		)}
																	</TableCell>
																	<TableCell>
																		<Chip
																			size="small"
																			label={
																				document.has_file ===
																				false
																					? 'Unavailable'
																					: 'Available'
																			}
																			sx={{
																				...getStatusColor(
																					document.has_file ===
																						false
																						? 'pending'
																						: 'success',
																				),
																				fontSize:
																					'9px',
																			}}
																		/>
																	</TableCell>
																	<TableCell align="right">
																		<Button
																			size="small"
																			onClick={() =>
																				void inspectDocument(
																					document,
																				)
																			}>
																			View
																			chunks
																		</Button>
																		<Button
																			size="small"
																			disabled={
																				document.has_file ===
																				false
																			}
																			onClick={() =>
																				void downloadDocument(
																					document,
																				)
																			}>
																			Download
																		</Button>
																		<IconButton
																			size="small"
																			color="error"
																			onClick={() =>
																				setConfirmState(
																					{
																						title: 'Delete document?',
																						message: `This permanently deletes ${getDocumentName(document)}, its chunks, vectors, and stored file.`,
																						actionLabel:
																							'Delete document',
																						onConfirm:
																							() =>
																								removeDocument(
																									document,
																								),
																					},
																				)
																			}>
																			<StudioIcon
																				path={
																					icons.delete
																				}
																			/>
																		</IconButton>
																	</TableCell>
																</TableRow>
															),
														)
													) : (
														<TableRow>
															<TableCell
																colSpan={4}
																align="center"
																sx={{ py: 7 }}>
																<Typography
																	fontSize="13px"
																	fontWeight={
																		600
																	}>
																	No documents
																	yet
																</Typography>
																<Typography
																	fontSize="11px"
																	color={GREY}
																	mt={0.5}>
																	Upload a
																	document to
																	add it to
																	the
																	bot&apos;s
																	knowledge.
																</Typography>
															</TableCell>
														</TableRow>
													)}
												</TableBody>
											</Table>
										</TableContainer>
									</Grid2>
								</Grid2>
							</Box>
						)}

						{tab === 'retrieval' && (
							<Box sx={{ p: { xs: 1.5, md: 2.25 } }}>
								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, lg: 8 }}>
										<Typography
											fontSize="15px"
											fontWeight={700}>
											Test knowledge retrieval
										</Typography>
										<Typography
											fontSize="11px"
											color={GREY}
											mt={0.4}
											mb={1.5}>
											Ask a real customer question to see
											which content the bot would receive.
										</Typography>
										<TextField
											fullWidth
											multiline
											minRows={3}
											placeholder="For example: What AI and IoT solutions does SenzMate provide?"
											value={question}
											onChange={event =>
												setQuestion(event.target.value)
											}
										/>
										<Grid2
											container
											justifyContent="flex-end"
											mt={1}>
											<Button
												variant="contained"
												disabled={
													!question.trim() ||
													Boolean(operation)
												}
												onClick={() =>
													void runRetrievalTest()
												}>
												{operation ===
												'Testing retrieval'
													? 'Testing…'
													: 'Run retrieval test'}
											</Button>
										</Grid2>
									</Grid2>
									<Grid2 size={{ xs: 12, lg: 4 }}>
										<Box
											sx={{
												p: 1.5,
												border: CARD_BORDER,
												borderRadius: '7px',
												height: '100%',
											}}>
											<Typography
												fontSize="12px"
												fontWeight={700}>
												Inspect by context ID
											</Typography>
											<Typography
												fontSize="10px"
												color={GREY}
												mt={0.4}
												mb={1.25}>
												Open a specific stored chunk for
												review or editing.
											</Typography>
											<Grid2
												container
												gap={1}
												wrap="nowrap">
												<TextField
													fullWidth
													size="small"
													placeholder="Context ID"
													value={contextId}
													onChange={event =>
														setContextId(
															event.target.value,
														)
													}
												/>
												<Button
													variant="outlined"
													disabled={
														!contextId.trim() ||
														Boolean(operation)
													}
													onClick={() =>
														void findContext()
													}>
													Find
												</Button>
											</Grid2>
										</Box>
									</Grid2>
								</Grid2>

								{operation === 'Testing retrieval' && (
									<Box sx={{ py: 5, textAlign: 'center' }}>
										<CircularProgress size={28} />
										<Typography
											fontSize="11px"
											color={GREY}
											mt={1}>
											Rewriting the question and ranking
											matching content…
										</Typography>
									</Box>
								)}

								{retrievalResult && (
									<Box mt={2.5}>
										<Box
											sx={{
												p: 1.75,
												borderRadius: '7px',
												backgroundColor: '#F7F9F9',
											}}>
											<Grid2
												container
												alignItems="center"
												justifyContent="space-between"
												gap={1}>
												<Box>
													<Typography
														fontSize="10px"
														fontWeight={700}
														color={GREY}>
														INTERPRETED QUESTION
													</Typography>
													<Typography
														fontSize="13px"
														fontWeight={600}
														mt={0.3}>
														{
															retrievalResult.rewritten_question
														}
													</Typography>
												</Box>
												<Chip
													label={`${retrievalResult.final_count} final matches`}
													size="small"
													sx={{
														...getStatusColor(
															'success',
														),
														fontWeight: 700,
													}}
												/>
											</Grid2>
											<Grid2
												container
												gap={0.75}
												mt={1.25}>
												{retrievalResult.categories.map(
													value => (
														<Chip
															key={value}
															size="small"
															label={value}
														/>
													),
												)}
												{retrievalResult.subcategories.map(
													value => (
														<Chip
															key={value}
															size="small"
															variant="outlined"
															label={value}
														/>
													),
												)}
											</Grid2>
										</Box>
										<Typography
											fontSize="13px"
											fontWeight={700}
											mt={2}
											mb={1}>
											Ranked content
										</Typography>
										<Grid2 container spacing={1.25}>
											{retrievalResult.documents.map(
												(document, index) => (
													<Grid2
														key={`${document.id}-${index}`}
														size={{
															xs: 12,
															md: 6,
														}}>
														<Box
															sx={{
																p: 1.5,
																border: CARD_BORDER,
																borderRadius:
																	'7px',
																height: '100%',
															}}>
															<Grid2
																container
																alignItems="center"
																justifyContent="space-between"
																gap={1}>
																<Typography
																	fontSize="12px"
																	fontWeight={
																		700
																	}>
																	#{index + 1}{' '}
																	{getChunkTitle(
																		document,
																	)}
																</Typography>
																<Grid2
																	container
																	gap={0.5}>
																	{document.category_label && (
																		<Chip
																			size="small"
																			label={
																				document.category_label
																			}
																			sx={{
																				fontSize:
																					'9px',
																			}}
																		/>
																	)}
																	<Chip
																		size="small"
																		label={formatLabel(
																			document.source,
																		)}
																		sx={{
																			...getStatusColor(
																				document.source,
																			),
																			fontSize:
																				'9px',
																		}}
																	/>
																</Grid2>
															</Grid2>
															<FormattedText
																text={
																	document.page_content
																}
																sx={{
																	fontSize:
																		'11px',
																	color: GREY,
																	mt: 1,
																	display:
																		'-webkit-box',
																	WebkitLineClamp: 7,
																	WebkitBoxOrient:
																		'vertical',
																	overflow:
																		'hidden',
																}}
															/>
															<Grid2
																container
																justifyContent="space-between"
																alignItems="center"
																mt={1}>
																<FormattedText
																	text={String(
																		document
																			.metadata
																			?.source_url ||
																			'',
																	)}
																	sx={{
																		maxWidth:
																			'72%',
																		fontSize:
																			'9px',
																		color: GREY,
																		overflow:
																			'hidden',
																		textOverflow:
																			'ellipsis',
																	}}
																/>
																{document.editable && (
																	<Button
																		size="small"
																		onClick={() =>
																			void openEditor(
																				document,
																			)
																		}>
																		Edit
																	</Button>
																)}
															</Grid2>
														</Box>
													</Grid2>
												),
											)}
										</Grid2>
									</Box>
								)}
							</Box>
						)}
					</Paper>
				</Box>
			</Box>

			<Dialog
				open={syncDialogOpen}
				onClose={() => setSyncDialogOpen(false)}
				maxWidth="xs"
				fullWidth>
				<DialogTitle fontSize="16px" fontWeight={700}>
					Sync entire sitemap
				</DialogTitle>
				<DialogContent>
					<Alert severity="info" sx={{ mb: 2, fontSize: '11px' }}>
						This processes scraping, chunking, and vector indexing
						and may take several minutes.
					</Alert>
					<Typography
						fontSize="11px"
						color={GREY}
						mb={1.5}
						sx={{ wordBreak: 'break-word' }}>
						{sitemapUrl}
					</Typography>
					<TextField
						fullWidth
						autoFocus
						type="password"
						label="Knowledge base password"
						value={syncPassword}
						onChange={event => setSyncPassword(event.target.value)}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setSyncDialogOpen(false)}>
						Cancel
					</Button>
					<Button
						variant="contained"
						disabled={!syncPassword}
						onClick={() => void runSitemapSync()}>
						Start sync
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={chunkDialogOpen}
				onClose={() => setChunkDialogOpen(false)}
				maxWidth="md"
				fullWidth
				PaperProps={{ sx: { height: { xs: '92vh', md: '82vh' } } }}>
				<DialogTitle
					sx={{
						fontSize: '16px',
						fontWeight: 700,
						borderBottom: CARD_BORDER,
					}}>
					{chunkDialogTitle}
					<Typography fontSize="10px" color={GREY} mt={0.3}>
						{chunks.length} content chunk(s)
					</Typography>
				</DialogTitle>
				<DialogContent
					sx={{ p: { xs: 1.5, md: 2 }, backgroundColor: '#F7F9F9' }}>
					{chunksLoading ? (
						<Box py={4}>
							{[1, 2, 3].map(item => (
								<Skeleton key={item} height={100} />
							))}
						</Box>
					) : chunks.length ? (
						chunks.map((chunk, index) => (
							<Paper
								key={`${getChunkId(chunk)}-${index}`}
								sx={{ ...cardSx, p: 1.75, mb: 1.25 }}>
								<Grid2
									container
									alignItems="flex-start"
									justifyContent="space-between"
									gap={1}>
									<Box sx={{ minWidth: 0, flex: 1 }}>
										<Grid2
											container
											alignItems="center"
											gap={0.75}>
											<Typography
												fontSize="13px"
												fontWeight={700}>
												{getChunkTitle(chunk)}
											</Typography>
											{getChunkId(chunk) !== null && (
												<Chip
													size="small"
													label={`ID ${getChunkId(chunk)}`}
													sx={{ fontSize: '9px' }}
												/>
											)}
										</Grid2>
										<FormattedText
											text={
												getChunkText(chunk) ||
												'No context text available.'
											}
											sx={{
												fontSize: '11px',
												color: GREY,
												mt: 0.8,
											}}
										/>
									</Box>
									<Grid2 container gap={0.25}>
										<Tooltip title="Edit chunk">
											<span>
												<IconButton
													size="small"
													disabled={
														getChunkId(chunk) ===
														null
													}
													onClick={() =>
														void openEditor(chunk)
													}>
													<StudioIcon
														path={icons.edit}
													/>
												</IconButton>
											</span>
										</Tooltip>
										<Tooltip title="Delete chunk permanently">
											<span>
												<IconButton
													size="small"
													color="error"
													disabled={
														getChunkId(chunk) ===
														null
													}
													onClick={() =>
														setConfirmState({
															title: 'Delete content chunk?',
															message: `Context ${getChunkId(chunk)} will be unlinked and permanently removed from the database and vector index.`,
															actionLabel:
																'Delete chunk',
															onConfirm: () =>
																removeContext(
																	chunk,
																),
														})
													}>
													<StudioIcon
														path={icons.delete}
													/>
												</IconButton>
											</span>
										</Tooltip>
									</Grid2>
								</Grid2>
							</Paper>
						))
					) : (
						<Box sx={{ py: 8, textAlign: 'center' }}>
							<Typography fontSize="13px" fontWeight={600}>
								No content chunks found
							</Typography>
							<Typography fontSize="11px" color={GREY} mt={0.5}>
								This source may not have been indexed yet.
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ borderTop: CARD_BORDER }}>
					<Button onClick={() => setChunkDialogOpen(false)}>
						Close
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={Boolean(editState)}
				onClose={() => setEditState(null)}
				maxWidth="md"
				fullWidth>
				<DialogTitle fontSize="16px" fontWeight={700}>
					Edit content chunk{' '}
					{editState ? getChunkId(editState.chunk) : ''}
				</DialogTitle>
				<DialogContent>
					{editState && (
						<Grid2 container spacing={1.5} mt={0.25}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									fullWidth
									label="Title"
									value={editState.values.title}
									onChange={event =>
										setEditState(current =>
											current
												? {
														...current,
														values: {
															...current.values,
															title: event.target
																.value,
														},
													}
												: current,
										)
									}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6, md: 3 }}>
								<TextField
									fullWidth
									label="Type"
									value={editState.values.context_type}
									onChange={event =>
										setEditState(current =>
											current
												? {
														...current,
														values: {
															...current.values,
															context_type:
																event.target
																	.value,
														},
													}
												: current,
										)
									}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6, md: 3 }}>
								<TextField
									fullWidth
									label="Subtype"
									value={editState.values.sub_type}
									onChange={event =>
										setEditState(current =>
											current
												? {
														...current,
														values: {
															...current.values,
															sub_type:
																event.target
																	.value,
														},
													}
												: current,
										)
									}
								/>
							</Grid2>
							<Grid2 size={12}>
								<TextField
									fullWidth
									multiline
									minRows={7}
									label="Context"
									value={editState.values.context}
									onChange={event =>
										setEditState(current =>
											current
												? {
														...current,
														values: {
															...current.values,
															context:
																event.target
																	.value,
														},
													}
												: current,
										)
									}
									helperText="Use re-embedding when the changed wording should affect search matching."
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									fullWidth
									multiline
									minRows={3}
									label="Brief summary"
									value={editState.values.brief_summary}
									onChange={event =>
										setEditState(current =>
											current
												? {
														...current,
														values: {
															...current.values,
															brief_summary:
																event.target
																	.value,
														},
													}
												: current,
										)
									}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									fullWidth
									multiline
									minRows={3}
									label="Keywords"
									value={editState.values.keywords}
									onChange={event =>
										setEditState(current =>
											current
												? {
														...current,
														values: {
															...current.values,
															keywords:
																event.target
																	.value,
														},
													}
												: current,
										)
									}
								/>
							</Grid2>
							{sourceOptions &&
								sourceOptions.options.length > 0 && (
									<Grid2 size={12}>
										<Divider sx={{ mb: 1.5 }} />
										<Typography
											fontSize="12px"
											fontWeight={700}
											mb={0.75}>
											Source URL and retrieval category
										</Typography>
										<Select
											fullWidth
											size="small"
											value={editState.sourceUrl}
											onChange={event =>
												setEditState(current =>
													current
														? {
																...current,
																sourceUrl:
																	event.target
																		.value,
															}
														: current,
												)
											}>
											<MenuItem value="">
												Automatic source selection
											</MenuItem>
											{sourceOptions.options.map(
												option => (
													<MenuItem
														key={option.url}
														value={option.url}>
														{option.category_label}{' '}
														· {option.url}
													</MenuItem>
												),
											)}
										</Select>
										<FormControlLabel
											control={
												<Checkbox
													checked={
														editState.reembedSource
													}
													onChange={event =>
														setEditState(current =>
															current
																? {
																		...current,
																		reembedSource:
																			event
																				.target
																				.checked,
																	}
																: current,
														)
													}
												/>
											}
											label={
												<Typography fontSize="10px">
													Re-embed after changing the
													source URL
												</Typography>
											}
										/>
									</Grid2>
								)}
							<Grid2 size={12}>
								<FormControlLabel
									control={
										<Checkbox
											checked={editState.keepEmbedding}
											onChange={event =>
												setEditState(current =>
													current
														? {
																...current,
																keepEmbedding:
																	event.target
																		.checked,
															}
														: current,
												)
											}
										/>
									}
									label={
										<Box>
											<Typography
												fontSize="11px"
												fontWeight={600}>
												Keep the existing embedding
											</Typography>
											<Typography
												fontSize="9px"
												color={GREY}>
												Use for wording corrections that
												should match the same questions
												as before.
											</Typography>
										</Box>
									}
								/>
							</Grid2>
						</Grid2>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setEditState(null)}>Cancel</Button>
					<Button
						variant="contained"
						disabled={Boolean(operation)}
						onClick={() => void saveContext()}>
						Save changes
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={Boolean(confirmState)}
				onClose={() => setConfirmState(null)}
				maxWidth="xs"
				fullWidth>
				<DialogTitle fontSize="16px" fontWeight={700}>
					{confirmState?.title}
				</DialogTitle>
				<DialogContent>
					<Alert severity="warning" sx={{ fontSize: '11px' }}>
						{confirmState?.message}
					</Alert>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setConfirmState(null)}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="error"
						disabled={Boolean(operation)}
						onClick={() => void confirmState?.onConfirm()}>
						{confirmState?.actionLabel}
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={Boolean(notice)}
				autoHideDuration={5000}
				onClose={() => setNotice(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
				<Alert
					severity={notice?.severity || 'info'}
					onClose={() => setNotice(null)}>
					{notice?.message}
				</Alert>
			</Snackbar>
		</>
	);
}
